import { difference, isEqual } from 'lodash';

import { SingleAsset, SingleAssetSettings } from '~/types/singleAsset';
import { ExtendedSegment } from '~/types/segments';

export const singleAssetFieldsBySegment = {
  drilling: ['rigId', 'wellId', 'rigAssetId', 'wellAssetId'],
  completion: ['fracFleetId', 'padId', 'completionWellAssetId'],
  drillout: ['drilloutUnitId'],
  intervention: ['interventionUnitId', 'interventionWellId', 'interventionWellAssetId'],
} as const;

export const allSingleAssetFields = Object.values(singleAssetFieldsBySegment).flat();

export const fieldsToCleanup = {
  drilling: difference(allSingleAssetFields, singleAssetFieldsBySegment.drilling),
  completion: difference(allSingleAssetFields, singleAssetFieldsBySegment.completion),
  drillout: difference(allSingleAssetFields, singleAssetFieldsBySegment.drillout),
  intervention: difference(allSingleAssetFields, singleAssetFieldsBySegment.intervention),
};

const parseNumberOrNull = (rawValue: string | number | null | undefined): number | null => {
  let parsedValue: number | null = null;

  if (rawValue !== null && rawValue !== 'null') {
    const maybeNumber = Number(rawValue);
    parsedValue = Number.isNaN(maybeNumber) ? null : maybeNumber;
  }

  return parsedValue || null;
};

export const pickSingleAssetFieldsBySegment = (
  singleAsset: SingleAsset,
  segment: ExtendedSegment
): SingleAssetSettings => {
  if (!segment || !singleAsset) {
    return {} as SingleAssetSettings;
  }

  const fieldsToPick = singleAssetFieldsBySegment[segment];

  // @ts-ignore
  return fieldsToPick.reduce((acc, currField) => {
    return { ...acc, [currField]: parseNumberOrNull(singleAsset[currField]) };
  }, {}) as SingleAssetSettings;
};

function cleanupSearchParams(
  searchParams: SingleAssetSettings,
  segment: ExtendedSegment
): SingleAssetSettings {
  const cleanedSearchParams = { ...searchParams };

  fieldsToCleanup[segment].forEach(field => {
    // @ts-ignore
    delete cleanedSearchParams[field];
  });

  return cleanedSearchParams;
}

export const queryParamsToString = (query: Record<string, any>) => {
  const searchParams = new URLSearchParams(
    Object.fromEntries(Object.entries(query).map(([key, value]) => [key, String(value)]))
  );

  return searchParams.toString();
};

export function getUpdatedUrl(singleAssetUpdate: SingleAsset, segment: ExtendedSegment): string {
  const url = new URL(window.location.href);
  const searchParams = Object.fromEntries(
    Object.entries(new URLSearchParams(url.search))
  ) as SingleAssetSettings;

  const currentUrlSingleAsset = pickSingleAssetFieldsBySegment(
    searchParams as SingleAsset,
    segment
  );
  const nextUrlSingleAsset = pickSingleAssetFieldsBySegment(singleAssetUpdate, segment);

  if (isEqual(currentUrlSingleAsset, nextUrlSingleAsset)) return null;

  // Ensure all values are strings when creating URLSearchParams
  const cleanedSearchParams = new URLSearchParams(
    Object.fromEntries(
      Object.entries(cleanupSearchParams(searchParams, segment)).map(([key, value]) => [
        key,
        String(value),
      ])
    )
  );

  Object.entries(nextUrlSingleAsset).forEach(([key, value]) =>
    cleanedSearchParams.set(key, String(value))
  );

  return `${url.pathname}?${cleanedSearchParams.toString()}`;
}

export const getSegmentBySingleAsset = (singleAsset: SingleAsset): ExtendedSegment | null => {
  if ('rigId' in singleAsset) return 'drilling';
  if ('fracFleetId' in singleAsset) return 'completion';
  if ('drilloutUnitId' in singleAsset) return 'drillout';
  if ('interventionUnitId' in singleAsset) return 'intervention';

  return null;
};

export const getSingleAssetObjectFromUrlSearch = (search: string): SingleAsset => {
  const searchParams = new URLSearchParams(search);
  const singleAssetFromUrl: Partial<SingleAsset> = {};

  allSingleAssetFields.forEach(field => {
    if (searchParams.has(field)) {
      singleAssetFromUrl[field] = parseNumberOrNull(searchParams.get(field));
    }
  });

  return singleAssetFromUrl as SingleAsset;
};

export const getUrlWithoutSingleAssetFields = () => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);

  const cleanedSearchParams = new URLSearchParams(
    // @ts-ignore
    Array.from(searchParams.entries()).filter(([key]) => !allSingleAssetFields.includes(key))
  );

  const cleanedSearchParamsString = cleanedSearchParams.toString();
  return cleanedSearchParamsString.length
    ? `${url.pathname}?${cleanedSearchParamsString}`
    : url.pathname;
};

export const checkIsNonEmptySingleAsset = (singleAssetFromUrl: SingleAsset) => {
  return Object.entries(singleAssetFromUrl).some(([_key, value]) => !!value);
};
